import type { TierLevel } from '../../domain/enums/TierLevel';
import type {
  CommissionLedgerEntryDto,
  CommissionSummaryDto,
  PaginatedResponseDto,
  ProcessWithdrawalResultDto,
} from '../../domain/contracts/api.contracts';
import { apiClient } from './apiClient';
import { normalizeMoney } from './wireAdapters';

/**
 * E09 query params. Friendly UI params (`month`, `commissionType`) are translated to the
 * canonical frozen wire names (`type`, `dateFrom`, `dateTo`) in `wireParams` — Phase-3 fix
 * for knowledge-tree §3 G0.3, where the client sent names the handler never read and the
 * filters silently no-oped. The handler additionally accepts the UI names as aliases.
 */
export interface CommissionLedgerParams {
  page?: number;
  limit?: number;
  /** UI-friendly month filter, `YYYY-MM` (invalid values are ignored, never 422). */
  month?: string;
  commissionType?: 'BOOKING_POOL' | 'INSTALLMENT_POOL';
  tier?: TierLevel;
  /** Canonical E09 names (orchestrator §6) — preferred when both are supplied. */
  type?: 'BOOKING_POOL' | 'INSTALLMENT_POOL';
  dateFrom?: string;
  dateTo?: string;
}

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

function lastDayOfMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  // Day 0 of the next month = last day of `month` (UTC, no DST involved).
  const day = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${month}-${String(day).padStart(2, '0')}`;
}

/** Translate UI params to the frozen E09 wire contract (`?page&limit&type&dateFrom&dateTo`). */
export function wireParams(params: CommissionLedgerParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (Number.isFinite(params.page)) out.page = Math.max(1, Math.floor(params.page as number));
  if (Number.isFinite(params.limit)) out.limit = Math.min(Math.max(1, Math.floor(params.limit as number)), 100);
  const type = params.type ?? params.commissionType;
  if (type === 'BOOKING_POOL' || type === 'INSTALLMENT_POOL') out.type = type;
  if (params.month && MONTH_RE.test(params.month)) {
    out.dateFrom = `${params.month}-01`;
    out.dateTo = lastDayOfMonth(params.month);
  } else {
    if (params.dateFrom) out.dateFrom = params.dateFrom;
    if (params.dateTo) out.dateTo = params.dateTo;
  }
  // `tier` is UI-only: E09 rejects unknown query params with 400 and has no tier filter (gap-report G-09).
  return out;
}

/** E09 — GET /commissions/my-ledger (paged + filters) */
export async function getMyLedger(
  params: CommissionLedgerParams,
): Promise<PaginatedResponseDto<CommissionLedgerEntryDto>> {
  const { data } = await apiClient.get<PaginatedResponseDto<CommissionLedgerEntryDto>>(
    '/commissions/my-ledger',
    { params: wireParams(params) },
  );
  return normalizeMoney(data);
}

/** E10 — GET /commissions/summary */
export async function getCommissionSummary(): Promise<CommissionSummaryDto> {
  const { data } = await apiClient.get<CommissionSummaryDto>('/commissions/summary');
  return normalizeMoney(data);
}


/**
 * POST /commissions/process-withdrawal — moves the WHOLE Available commission (less the 5% service charge) into
 * Available for withdrawal. No body; the `Idempotency-Key` is required, and a replay of the same key returns the
 * stored result without processing twice.
 */
export async function processWithdrawal(idempotencyKey: string): Promise<ProcessWithdrawalResultDto> {
  const { data } = await apiClient.post<ProcessWithdrawalResultDto>(
    '/commissions/process-withdrawal',
    undefined,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return normalizeMoney(data);
}
